import { Badge, Label, StringSelect, MultiSelect } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { NewFormsExperienceMode, PortalSettings } from "../../../shared/schema";
import { FormSelectOption } from "./FormSelectOption";
import { NewFormsExperienceListHint } from "./NewFormsExperienceListHint";
import { useNewFormsExperienceForms } from "./useNewFormsExperienceForms";

interface NewFormsExperienceSectionProps {
  settings: PortalSettings;
  onUpdateSettings: (updates: Partial<PortalSettings>) => void;
}

export const NewFormsExperienceSection = ({ settings, onUpdateSettings }: NewFormsExperienceSectionProps) => {
  const { t } = useTranslation("portal");

  const mode = settings.newFormsExperience ?? "off";
  const selectedFormIds = settings.newFormsExperienceFormIds ?? [];
  const usesFormList = mode === "selected" || mode === "all_except";

  const { searchQuery, setSearchQuery, formsById, isLoading } = useNewFormsExperienceForms({
    enabled: usesFormList,
    selectedFormIds,
  });

  const optionIds = [...new Set([...selectedFormIds, ...formsById.keys()])];
  const options = optionIds.map((id) => {
    const form = formsById.get(id);
    return { label: form ? (form.displayTitle ? `${form.title} (${form.displayTitle})` : form.title) : id, value: id };
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">{t("New Forms Experience")}</Label>
            <Badge variant="lab-normal" className="uppercase tracking-wide">
              {t("Beta")}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("Patients fill out forms with a faster and more accessible experience in the portal.")}
          </p>
        </div>
        <StringSelect
          value={mode}
          onValueChange={(value: NewFormsExperienceMode) => onUpdateSettings({ newFormsExperience: value })}
          options={[
            { value: "off", label: t("Off") },
            { value: "all", label: t("All forms") },
            { value: "selected", label: t("Only certain forms") },
            { value: "all_except", label: t("All forms except") },
          ]}
          className="w-44 flex-shrink-0"
        />
      </div>

      {usesFormList && (
        <div className="space-y-1 pl-5">
          <MultiSelect
            options={options}
            value={selectedFormIds}
            onValueChange={(formIds) => onUpdateSettings({ newFormsExperienceFormIds: formIds })}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            shouldFilter={false}
            isLoading={isLoading}
            placeholder={mode === "selected" ? t("Choose forms to include...") : t("Choose forms to exclude...")}
            searchPlaceholder={t("Search by name, description, or ID")}
            emptyText={t("No forms found")}
            renderOption={(option) => (
              <FormSelectOption
                title={option.label}
                description={formsById.get(option.value)?.description}
                formId={option.value}
              />
            )}
            showAsBadges
          />
          <NewFormsExperienceListHint isExclusionList={mode === "all_except"} />
        </div>
      )}
    </div>
  );
};
